/**
 * Parsing Health Auto Export payloads.
 *
 * DESIGN: tolerant, and loud about what it did not understand.
 *
 * Health Auto Export names metrics as a snake_case form of the Apple Health
 * name, but the exact strings are not contractually documented and have
 * changed between app versions. Guessing wrong silently would mean weight
 * quietly never arriving. So this parser:
 *
 *   - matches on a table of known aliases, not a single expected string;
 *   - falls back to fuzzy matching on distinctive substrings;
 *   - COLLECTS every metric name it could not place into `unmapped`, which the
 *     app surfaces in Settings so the real field name can be read off the wire
 *     and added here in one line.
 *
 * Dates arrive as "yyyy-MM-dd HH:mm:ss Z" (e.g. "2026-02-06 14:30:00 -0800"),
 * which is NOT ISO-8601 -- `new Date()` parses it inconsistently across
 * engines, so it is converted explicitly.
 *
 * Heart-rate-family metrics carry `Min`/`Avg`/`Max` (capitalised) instead of a
 * bare `qty`. Sleep arrives pre-aggregated with hours as decimals.
 */
import { toDay } from '../lib/time';
import type { Day, Reading, Run, Source } from './schema';

// --------------------------------------------------------------------------
// Date handling
// --------------------------------------------------------------------------

/**
 * Parse Health Auto Export's date format, falling back to ISO.
 * Returns null rather than an Invalid Date so callers must handle it.
 */
export function parseHaeDate(value: unknown): Date | null {
  if (typeof value !== 'string' || !value.trim()) return null;
  const s = value.trim();
  // "2026-02-06 14:30:00 -0800" / "+0530" / " +05:30"
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})(?:\.\d+)?\s*([+-]\d{2}:?\d{2}|Z)?$/.exec(s);
  if (m) {
    const [, y, mo, d, h, mi, sec, off] = m;
    let offset = 'Z';
    if (off && off !== 'Z') offset = off.includes(':') ? off : `${off.slice(0, 3)}:${off.slice(3)}`;
    else if (!off) offset = '';
    const iso = `${y}-${mo}-${d}T${h}:${mi}:${sec}${offset}`;
    const parsed = new Date(iso);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  const parsed = new Date(s);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

// --------------------------------------------------------------------------
// Metric aliases
// --------------------------------------------------------------------------

/** Canonical field -> the metric names that have been seen to carry it. */
const ALIASES: Record<string, string[]> = {
  weightKg: ['weight_body_mass', 'body_mass', 'weight', 'body_weight'],
  bodyFatPct: ['body_fat_percentage', 'body_fat', 'bodyfat_percentage'],
  leanMassKg: ['lean_body_mass', 'lean_mass'],
  restingHr: ['resting_heart_rate', 'resting_hr'],
  hrvMs: ['heart_rate_variability', 'heart_rate_variability_sdnn', 'hrv', 'hrv_sdnn'],
  steps: ['step_count', 'steps'],
  activeKcal: ['active_energy', 'active_energy_burned', 'active_calories'],
  spo2Pct: ['blood_oxygen_saturation', 'oxygen_saturation', 'spo2'],
};

/** Distinctive substrings used when no alias matched exactly. */
const FUZZY: [string, RegExp][] = [
  ['weightKg', /body_?mass(?!_index)|^weight/],
  ['bodyFatPct', /body_?fat/],
  ['leanMassKg', /lean/],
  ['restingHr', /resting.*(heart|hr)/],
  ['hrvMs', /variability|hrv/],
  ['steps', /step/],
  ['activeKcal', /active.*(energy|cal)/],
  ['spo2Pct', /oxygen|spo2/],
];

function canonicalField(name: string): string | null {
  const key = name.toLowerCase().trim();
  for (const [field, names] of Object.entries(ALIASES)) {
    if (names.includes(key)) return field;
  }
  for (const [field, pattern] of FUZZY) {
    if (pattern.test(key)) return field;
  }
  return null;
}

// --------------------------------------------------------------------------
// Unit conversion
// --------------------------------------------------------------------------

const LB_PER_KG = 2.20462262185;

/** Normalise a mass to kilograms based on the payload's stated unit. */
export function massToKg(qty: number, units: string | undefined): number {
  const u = (units ?? '').toLowerCase();
  if (u === 'lb' || u === 'lbs' || u === 'pound' || u === 'pounds') return qty / LB_PER_KG;
  if (u === 'g') return qty / 1000;
  if (u === 'st' || u === 'stone') return (qty * 14) / LB_PER_KG;
  return qty; // kg, or unspecified -- HAE defaults to the user's Health units
}

/** Normalise a distance to metres. */
export function distanceToMeters(qty: number, units: string | undefined): number {
  const u = (units ?? '').toLowerCase();
  if (u === 'mi' || u === 'mile' || u === 'miles') return qty * 1609.344;
  if (u === 'km') return qty * 1000;
  if (u === 'ft') return qty * 0.3048;
  if (u === 'yd') return qty * 0.9144;
  return qty; // metres
}

// --------------------------------------------------------------------------
// Parsing
// --------------------------------------------------------------------------

export interface ParsedHealth {
  /** Per-day field values, ready to merge into DailyMetrics. */
  days: Map<Day, Record<string, Reading>>;
  /** Runs and other distance workouts. */
  runs: Run[];
  /** Metric names the parser did not recognise, for the user to inspect. */
  unmapped: string[];
  /** Problems worth reporting without failing the whole import. */
  warnings: string[];
}

interface DataPoint { qty?: number; date?: string; Min?: number; Avg?: number; Max?: number; source?: string }

/** Pull the representative number from a data point, preferring Avg for HR-family. */
function pointValue(point: DataPoint, field: string): number | undefined {
  if (typeof point.qty === 'number') return point.qty;
  // Heart-rate-family points carry Min/Avg/Max instead of qty.
  if (typeof point.Avg === 'number') return point.Avg;
  if (field === 'restingHr' && typeof point.Min === 'number') return point.Min;
  if (typeof point.Max === 'number') return point.Max;
  return undefined;
}

/** Fields that are summed across a day rather than taking the latest value. */
const CUMULATIVE = new Set(['steps', 'activeKcal']);

/**
 * Parse a Health Auto Export payload.
 * Accepts either the full `{ data: { metrics, workouts } }` envelope or a bare
 * `{ metrics, workouts }`, because both shapes appear in the wild.
 */
export function parseHealthPayload(payload: unknown, now: Date = new Date()): ParsedHealth {
  const out: ParsedHealth = { days: new Map(), runs: [], unmapped: [], warnings: [] };
  if (!payload || typeof payload !== 'object') {
    out.warnings.push('Payload was not an object.');
    return out;
  }
  const root = payload as Record<string, unknown>;
  const data = (root.data && typeof root.data === 'object' ? root.data : root) as Record<string, unknown>;

  const metrics = Array.isArray(data.metrics) ? data.metrics : [];
  const workouts = Array.isArray(data.workouts) ? data.workouts : [];
  if (!metrics.length && !workouts.length) out.warnings.push('Payload contained no metrics or workouts.');

  const seenUnmapped = new Set<string>();

  for (const raw of metrics) {
    const metric = raw as { name?: string; units?: string; data?: DataPoint[] };
    const name = typeof metric.name === 'string' ? metric.name : '';
    if (!name) continue;
    const field = canonicalField(name);
    if (!field) {
      if (!seenUnmapped.has(name)) { seenUnmapped.add(name); out.unmapped.push(name); }
      continue;
    }
    const points = Array.isArray(metric.data) ? metric.data : [];
    for (const point of points) {
      const when = parseHaeDate(point.date);
      if (!when) { out.warnings.push(`Unparseable date on ${name}: ${String(point.date)}`); continue; }
      const value = pointValue(point, field);
      if (typeof value !== 'number' || !Number.isFinite(value)) continue;

      const day = toDay(when);
      const normalised =
        field === 'weightKg' || field === 'leanMassKg' ? massToKg(value, metric.units) : value;

      const bucket = out.days.get(day) ?? {};
      const existing = bucket[field];
      if (existing && CUMULATIVE.has(field)) {
        // Several partial-day samples: sum them.
        bucket[field] = { value: existing.value + normalised, source: 'apple_health', at: when.toISOString() };
      } else if (!existing || existing.at < when.toISOString()) {
        bucket[field] = { value: normalised, source: 'apple_health', at: when.toISOString() };
      }
      out.days.set(day, bucket);
    }
  }

  // Sleep arrives as its own aggregated metric shape, not a qty series.
  for (const raw of metrics) {
    const metric = raw as { name?: string; data?: Record<string, unknown>[] };
    if (!/sleep/i.test(metric.name ?? '')) continue;
    for (const entry of metric.data ?? []) {
      const end = parseHaeDate(entry.sleepEnd) ?? parseHaeDate(entry.date as string);
      if (!end) continue;
      const day = toDay(end);
      const bucket = out.days.get(day) ?? {};
      const hoursToSec = (h: unknown) => (typeof h === 'number' && Number.isFinite(h) ? Math.round(h * 3600) : undefined);
      const at = end.toISOString();
      const totalSleep = hoursToSec(entry.totalSleep ?? entry.asleep);
      if (totalSleep !== undefined) bucket.totalSleepSec = { value: totalSleep, source: 'apple_health', at };
      const rem = hoursToSec(entry.rem);
      if (rem !== undefined) bucket.remSleepSec = { value: rem, source: 'apple_health', at };
      const deep = hoursToSec(entry.deep);
      if (deep !== undefined) bucket.deepSleepSec = { value: deep, source: 'apple_health', at };
      out.days.set(day, bucket);
    }
  }

  for (const raw of workouts) {
    const w = raw as Record<string, unknown>;
    const name = typeof w.name === 'string' ? w.name : '';
    const start = parseHaeDate(w.start);
    if (!start) continue;
    const distance = w.distance as { qty?: number; units?: string } | undefined;
    const distanceM = typeof distance?.qty === 'number' ? distanceToMeters(distance.qty, distance.units) : 0;
    // Only distance-bearing activities become runs; strength work is logged
    // in the app's own session model, not imported as a "run".
    const isRun = /run|jog|walk|hike|cycl|bike/i.test(name);
    if (!isRun || distanceM <= 0) continue;

    const durationSec = typeof w.duration === 'number' ? w.duration : 0;
    const hr = Array.isArray(w.heartRateData) ? (w.heartRateData as DataPoint[]) : [];
    const avgHr = hr.length ? hr.reduce((s, p) => s + (p.Avg ?? 0), 0) / hr.length : undefined;
    const maxHr = hr.length ? Math.max(...hr.map((p) => p.Max ?? 0)) : undefined;
    const energy = w.activeEnergyBurned as { qty?: number } | undefined;

    const route = Array.isArray(w.route)
      ? downsampleRoute(w.route as { latitude?: number; longitude?: number }[])
      : undefined;

    const run: Run = {
      id: `hae-${String(w.id ?? start.toISOString())}`,
      day: toDay(start),
      startedAt: start.toISOString(),
      durationSec,
      distanceM,
      source: 'apple_health' as Source,
      externalId: typeof w.id === 'string' ? w.id : undefined,
      note: name,
    };
    if (avgHr && avgHr > 0) run.avgHr = Math.round(avgHr);
    if (maxHr && maxHr > 0) run.maxHr = Math.round(maxHr);
    if (typeof energy?.qty === 'number') run.activeKcal = energy.qty;
    if (typeof w.elevationUp === 'number') run.elevationGainM = w.elevationUp;
    if (route?.length) run.route = route;
    out.runs.push(run);
  }

  void now;
  return out;
}

/**
 * Reduce a GPS track to at most `max` points.
 * A one-hour run sampled at 1 Hz is 3,600 points; storing every one of them
 * for every run would dominate the database for no visible benefit.
 */
export function downsampleRoute(
  points: { latitude?: number; longitude?: number }[], max = 200,
): { lat: number; lon: number }[] {
  const valid = points.filter(
    (p): p is { latitude: number; longitude: number } =>
      typeof p.latitude === 'number' && typeof p.longitude === 'number',
  );
  if (valid.length <= max) return valid.map((p) => ({ lat: p.latitude, lon: p.longitude }));
  const step = valid.length / max;
  const out: { lat: number; lon: number }[] = [];
  for (let i = 0; i < max; i++) {
    const p = valid[Math.floor(i * step)]!;
    out.push({ lat: p.latitude, lon: p.longitude });
  }
  const last = valid[valid.length - 1]!;
  out.push({ lat: last.latitude, lon: last.longitude });
  return out;
}
